'use client'

import { Suspense, useEffect } from 'react'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { useToast } from '@/lib/context/ToastContext'
import { analytics } from '@/lib/analytics'
import { clearAuthSession } from '@/lib/services/auth'

/**
 * Handles auth-related URL parameters and shows appropriate toast messages:
 * - ?auth_recovery=pkce  → PKCE failure: link opened in different browser
 * - ?auth_error=...      → Generic auth error from callback
 * - ?auth_success=true    → Successful authentication
 * - auth:session-restore-failed event → Session corrupted, offer recovery
 *
 * Mounted in the root layout inside ToastProvider + LanguageProvider.
 */
function AuthErrorHandlerInner() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const { t } = useLanguage()
  const toast = useToast()

  // Listen for session restore failures dispatched from AuthContext
  useEffect(() => {
    const handleSessionRestoreFailed = () => {
      toast.error(t('authModal.authSessionRestoreFailed'), {
        duration: 15000,
        action: {
          label: t('authModal.authClearSessionAndRetry'),
          onClick: () => {
            clearAuthSession()
            window.location.reload()
          },
        },
      })
    }

    window.addEventListener('auth:session-restore-failed', handleSessionRestoreFailed)
    return () => {
      window.removeEventListener('auth:session-restore-failed', handleSessionRestoreFailed)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const authRecovery = searchParams.get('auth_recovery')
    const authError = searchParams.get('auth_error')
    const authSuccess = searchParams.get('auth_success')

    if (!authRecovery && !authError && !authSuccess) return

    // Server-side analytics context passed through redirect params
    const errorCode = searchParams.get('auth_error_code') || undefined
    const flowType = (searchParams.get('auth_flow') || 'code') as 'code' | 'token_hash'
    const browserClass = searchParams.get('auth_browser') || undefined
    const noVerifier = searchParams.get('auth_no_verifier') === '1'

    // Clean ALL auth-related URL params (remove auth-related params, keep others)
    const params = new URLSearchParams(searchParams.toString())
    params.delete('auth_recovery')
    params.delete('auth_error')
    params.delete('auth_success')
    params.delete('auth_error_code')
    params.delete('auth_flow')
    params.delete('auth_browser')
    params.delete('auth_no_verifier')
    const cleanUrl = params.toString() ? `${pathname}?${params}` : pathname
    router.replace(cleanUrl, { scroll: false })

    if (authRecovery === 'pkce') {
      toast.error(t('authModal.authErrorPkce'), {
        duration: 15000,
        action: {
          label: t('authModal.authClearSessionAndRetry'),
          onClick: () => {
            clearAuthSession()
            window.location.reload()
          },
        },
      })
      analytics.auth.pkceFailure({
        error_code: errorCode,
        browser_class: browserClass,
        has_code_verifier: !noVerifier,
        user_agent: navigator.userAgent,
      })
      return
    }

    if (authError) {
      // A one-time link/code that was already used is not a failure the user
      // caused and not something "try again" fixes – say so, and hand them the
      // one action that works (request a fresh link).
      const isConsumedToken = errorCode === 'otp_expired' || errorCode === 'token_not_found'

      if (isConsumedToken) {
        toast.error(t('authModal.authErrorLinkAlreadyUsed'), {
          duration: 15000,
          action: {
            label: t('authModal.authRequestNewLink'),
            onClick: () => {
              const returnUrl = encodeURIComponent(pathname)
              router.push(`/login?returnUrl=${returnUrl}`)
            },
          },
        })
      } else {
        toast.error(t('authModal.authErrorGeneric'), { duration: 8000 })
      }

      analytics.auth.callbackFailure({
        flow_type: flowType,
        error_code: errorCode,
        error_message: authError,
        browser_class: browserClass,
        has_code_verifier: !noVerifier,
      })
      return
    }

    if (authSuccess) {
      toast.success(t('authModal.authSuccessSignedIn'))
      return
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  return null
}

export default function AuthErrorHandler() {
  return (
    <Suspense fallback={null}>
      <AuthErrorHandlerInner />
    </Suspense>
  )
}
